import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Target, Users, Grid2X2 } from "lucide-react";
import { SectionCard, TextRow, ListEditor, RowsEditor } from "@/components/Brand/editors";

const SWOT_QUADRANTS = [
  { key: "strengths", label: "Strengths", tone: "border-emerald-500/40" },
  { key: "weaknesses", label: "Weaknesses", tone: "border-red-500/40" },
  { key: "opportunities", label: "Opportunities", tone: "border-sky-500/40" },
  { key: "threats", label: "Threats", tone: "border-amber-500/40" },
];

export default function BrandStrategyTab({ brand, onSaved }) {
  const [strategy, setStrategy] = useState(brand.strategy || {});
  const [swot, setSwot] = useState(brand.swot || {});
  const [busy, setBusy] = useState(false);
  const setS = (k, v) => setStrategy((s) => ({ ...s, [k]: v }));

  const save = async (payload) => {
    setBusy(true);
    try {
      await api.patch(`/brands/${brand.id}`, payload);
      toast.success("Saved");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="brand-strategy-tab">
      <SectionCard title="Strategy & objectives" icon={Target} onSave={() => save({ strategy })} busy={busy} testid="brand-objectives-section">
        <TextRow label="Target market" value={strategy.target_market} onChange={(v) => setS("target_market", v)} multiline testid="strategy-market" />
        <RowsEditor label="Objectives & KPIs"
          columns={[{ key: "title", label: "Objective" }, { key: "kpi", label: "KPI" }, { key: "target", label: "Target" }]}
          rows={strategy.objectives || []} onChange={(v) => setS("objectives", v)} addLabel="Add objective" testid="objectives" />
      </SectionCard>

      <SectionCard title="Audience personas" icon={Users} onSave={() => save({ strategy })} busy={busy} testid="brand-personas-section">
        <RowsEditor
          columns={[
            { key: "name", label: "Persona name" },
            { key: "channels", label: "Preferred channels" },
            { key: "demographics", label: "Demographics", type: "textarea" },
            { key: "psychographics", label: "Psychographics", type: "textarea" },
            { key: "pain_points", label: "Pain points", type: "textarea" },
          ]}
          rows={strategy.personas || []} onChange={(v) => setS("personas", v)} addLabel="Add persona" testid="personas" />
      </SectionCard>

      <SectionCard title="SWOT analysis" icon={Grid2X2} onSave={() => save({ swot })} busy={busy} testid="brand-swot-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SWOT_QUADRANTS.map((q) => (
            <div key={q.key} className={`border ${q.tone} p-3`}>
              <ListEditor label={q.label} items={swot[q.key] || []} onChange={(v) => setSwot((s) => ({ ...s, [q.key]: v }))} placeholder={`Add ${q.label.toLowerCase().slice(0, -1)}…`} testid={`swot-${q.key}`} />
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
